import type { Message, Part, Session } from "@mux/sdk/v2"
import type { MuxClient } from "@mux/sdk/v2/client"
import { jsonArray, jsonObject, jsonString, optionalJsonString } from "@/transport/decode"
import { postCreate, sdkRequest, sdkResultError } from "@/transport/sdk-request"
import { sessionDecoder } from "@/transport/session-decode"
import { Identifier } from "@/utils/id"

/** A chat the machine just created from another chat's history, with the latest page of its transcript. */
export type SessionSnapshot = {
  session: Session
  messages: Array<{ info: Message; parts: Part[] }>
  hasMore: boolean
  nextCursor?: string
}

const INVALID_RESPONSE = "Server returned an invalid session snapshot"

const decodeSession = sessionDecoder(INVALID_RESPONSE)

// The snapshot decoders check only that every message and part belongs to the
// requested chat. Their contents follow the same rules as ordinary transcript
// pages, because a fork copies saved history in the shapes it was saved in.
function decodeMessage(value: unknown, expectedSessionID: string): Message {
  const info = jsonObject(value, INVALID_RESPONSE)
  jsonString(info.id, INVALID_RESPONSE)
  if (jsonString(info.sessionID, INVALID_RESPONSE) !== expectedSessionID) throw new Error(INVALID_RESPONSE)
  if (info.role !== "user" && info.role !== "assistant") throw new Error(INVALID_RESPONSE)
  return info as Message
}

function decodePart(value: unknown, expectedSessionID: string, expectedMessageID: string): Part {
  const part = jsonObject(value, INVALID_RESPONSE)
  jsonString(part.id, INVALID_RESPONSE)
  jsonString(part.type, INVALID_RESPONSE)
  if (
    jsonString(part.sessionID, INVALID_RESPONSE) !== expectedSessionID ||
    jsonString(part.messageID, INVALID_RESPONSE) !== expectedMessageID
  ) throw new Error(INVALID_RESPONSE)
  return part as Part
}

function decodeItems(value: unknown, expectedSessionID: string) {
  return jsonArray(value, INVALID_RESPONSE).map((value) => {
    const item = jsonObject(value, INVALID_RESPONSE)
    const info = decodeMessage(item.info, expectedSessionID)
    const parts = jsonArray(item.parts, INVALID_RESPONSE).map((value) =>
      decodePart(value, expectedSessionID, info.id),
    )
    return { info, parts }
  })
}

export function decodeSessionSnapshot(value: unknown, expectedSessionID: string): SessionSnapshot {
  const snapshot = jsonObject(value, INVALID_RESPONSE)
  const session = decodeSession(snapshot.session)
  if (session.id !== expectedSessionID) throw new Error(INVALID_RESPONSE)
  const messages = decodeItems(snapshot.messages, expectedSessionID)
  if (typeof snapshot.hasMore !== "boolean") throw new Error(INVALID_RESPONSE)
  return {
    session,
    messages,
    hasMore: snapshot.hasMore,
    nextCursor: optionalJsonString(snapshot.nextCursor, INVALID_RESPONSE) || undefined,
  }
}

/**
 * The latest page of a chat's transcript that ends with `messageID`: the
 * history a fork at that message starts with.
 */
export async function loadTranscriptThrough(
  client: MuxClient,
  input: { sessionID: string; messageID: string; limit: number; signal?: AbortSignal },
): Promise<SessionSnapshot["messages"]> {
  const result = await sdkRequest(client, "get", {
    url: `/session/${encodeURIComponent(input.sessionID)}/message`,
    query: { through: input.messageID, limit: input.limit },
    signal: input.signal,
  })
  if (result.data === undefined) throw sdkResultError(result, "Failed to load transcript")
  return decodeItems(result.data, input.sessionID)
}

export function startSessionFromStarter(
  client: MuxClient,
  input: { sourceSessionID: string; messageID: string; sessionID: string; signal?: AbortSignal },
): Promise<SessionSnapshot> {
  return postCreate(client, {
    url: `/session/${encodeURIComponent(input.sourceSessionID)}/starter/${encodeURIComponent(input.messageID)}/start`,
    body: { sessionID: input.sessionID },
    name: "Starter request",
    decode: (data) => decodeSessionSnapshot(data, input.sessionID),
    signal: input.signal,
  })
}

/** Forks a chat at `messageID` with an optional title; an empty title keeps the automatic title lifecycle. */
export function forkSessionAtMessage(
  client: MuxClient,
  input: { sessionID: string; messageID: string; title: string; forkID?: string; hidden?: boolean; signal?: AbortSignal },
): Promise<SessionSnapshot> {
  const forkID = input.forkID ?? Identifier.ascending("session")
  return postCreate(client, {
    url: `/session/${encodeURIComponent(input.sessionID)}/fork`,
    body: { messageID: input.messageID, title: input.title, forkID, hidden: input.hidden ?? false },
    name: "Fork",
    decode: (data) => decodeSessionSnapshot(data, forkID),
    signal: input.signal,
  })
}
